<script setup lang="ts">
import { faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import LoginStepWrapper from '../LoginStepWrapper.vue';
import { AuthStep } from '@store/auth';

const { tokenDisabled } = defineProps<{
    tokenDisabled?: boolean;
}>();

const emit = defineEmits<{
    submit: [step: AuthStep];
    back: [];
}>();

const authStore = useAuthStore();
const { loading, error } = classToRefs(authStore);
const analyticsStore = useAnalyticsStore();
const gtm = useGtm();
const password = ref('');
const isSubmit = ref(true);

const mobileOrEmail = defineModel<string>('username', { default: '' });
const showPassword = ref(false);
const submitForm = async () => {
    const username = authStore.validateUsername(mobileOrEmail.value);
    if (!username || !password.value) {
        analyticsStore.trackAll('login_result', {
            method: 'password',
            status: 'validation_failed',
        });
        return;
    }
    analyticsStore.trackAll('login_submit', { method: 'password' });
    isSubmit.value = true;
    try {
        const result = await authStore.loginByPassword({
            password: password.value,
            username: mobileOrEmail.value,
        });
        analyticsStore.trackAll('login_result', {
            method: 'password',
            status: 'success',
        });
        analyticsStore.trackAll('logIn');
        gtm?.trackEvent({
            event: 'login button',
        });
        emit('submit', result);
    } catch (e) {
        analyticsStore.trackAll('login_result', {
            method: 'password',
            status: 'failed',
        });
        reportException(e);
    }
};

function forgotPassword() {
    isSubmit.value = false;
    emit('submit', AuthStep.ChangePasswordMobile);
}

const loginWithToken = () => emit('submit', AuthStep.EnterMobileOrEmail);

onMounted(() => {
    analyticsStore.trackAll('login_password_view');
});
</script>

<template>
    <!-- The form for login with password -->
    <LoginStepWrapper
        title="ورود با رمز عبور"
        sub-title="شماره موبایل یا آدرس ایمیل به همراه کلمه عبور خود را وارد کنید."
        @submit="submitForm"
    >
        <template #inputs="{ focus }">
            <mr-input
                v-model="mobileOrEmail"
                input-id="phonenumber"
                type="text"
                placeholder="شماره موبایل یا ایمیل"
                dir="ltr"
                :error-text="error.MobileOrEmail || ''"
                :hide-description="!error.MobileOrEmail"
                :invalid="!!error.MobileOrEmail"
                required
                name="username"
                autofocus
                @focus="focus"
            />
            <mr-input
                v-model="password"
                :type="showPassword ? undefined : 'password'"
                required
                placeholder="رمز ورود"
                :error-text="error.CurrentPassword || ''"
                :hide-description="!error.CurrentPassword"
                dir="ltr"
                :invalid="!!error.CurrentPassword"
                :icon="showPassword ? faEyeSlash : faEye"
                @click:icon="showPassword = !showPassword"
                @submit="submitForm"
                @focus="focus"
            />
            <div class="side-actions">
                <mr-button
                    variant="transparent"
                    size="small"
                    :loading="!isSubmit && loading"
                    class="side-action-button"
                    @click="loginWithToken"
                >
                    ثبت‌نام در مِستربلیط
                </mr-button>
                <mr-button
                    variant="transparent"
                    size="small"
                    class="side-action-button"
                    @click="forgotPassword"
                >
                    فراموشی رمز عبور
                </mr-button>
            </div>
        </template>
        <template #actions>
            <mr-button
                full-width
                :loading="isSubmit && loading"
                type="submit"
                :disabled="tokenDisabled || !password"
                @click="analyticsStore.trackAll('login_password_click')"
            >
                ورود به حساب کاربری
            </mr-button>
        </template>
    </LoginStepWrapper>
</template>

<style scoped lang="scss">
.hidden {
    display: none;
}
:deep(.input-container) {
    background-color: white !important;
    border-radius: 8px;
}
.side-actions {
    display: flex;
    margin-bottom: 16px;
    justify-content: space-between;
    align-items: center;
    @include mobile-only {
        margin-bottom: 12px;
    }
    .side-action-button {
        white-space: nowrap;
    }
}
</style>
